import React, { memo, useMemo } from "react";
import { UserCheck, ChevronRight, ShieldAlert, Shirt, FileText, PlayingCardsFan, MessageSquareQuote, Users, PanelRightOpen, Award } from "lucide-react";
const RightSidebar = memo(({
  isRightSidebarOpen,
  setIsRightSidebarOpen,
  activeClass,
  activeStudents = [],
  activeTab = "classroom",
  onTabChange,
}) => {
  const stats = useMemo(() => {
    const infractionCount = activeStudents.filter(
      (st) => st.has_uniform_infraction || st.uniform_infraction
    ).length;

    return {
      total: activeStudents.length,
      infractionCount,
    };
  }, [activeStudents]);

  if (!activeClass) return null;

  return (
    <aside
      className={`bg-white border-l border-zinc-200 shrink-0 shadow-2xs z-10 overflow-hidden transition-all duration-300 ease-in-out text-zinc-800 ${
        isRightSidebarOpen ? "w-72 opacity-100" : "w-0 opacity-0 pointer-events-none border-l-0"
      }`}
    >
      <div className="w-72 p-4 flex flex-col gap-4 h-full overflow-y-auto">
        <div className="flex items-center justify-between border-b border-zinc-200 pb-3">
          <span className="text-xs font-bold text-zinc-800 uppercase tracking-wider">
            Subject Summary
          </span>
          <button
            type="button"
            onClick={() => setIsRightSidebarOpen(false)}
            className="p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <PanelRightOpen className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="p-3.5 rounded-lg border border-zinc-200 bg-zinc-50 space-y-2.5 shadow-2xs">
            <span className="text-xs font-bold text-zinc-800 block border-b border-zinc-200 pb-1.5">
              Class Roster Stats
            </span>
            <div className="flex justify-between text-xs text-zinc-600">
              <span>Total Enrolled</span>
              <span className="font-bold text-zinc-900">{stats.total}</span>
            </div>
            <div className="flex justify-between text-xs text-zinc-600 pt-1 border-t border-zinc-200/60">
              <span>Uniform Infractions</span>
              <span className={`font-bold ${stats.infractionCount > 0 ? "text-amber-600" : "text-zinc-500"}`}>
                {stats.infractionCount}
              </span>
            </div>
          </div>

          <div className="p-3.5 rounded-lg border border-zinc-200 bg-zinc-50 space-y-2 shadow-2xs">
            <span className="text-xs font-bold text-zinc-800 block border-b border-zinc-200 pb-1.5">
              View Navigation
            </span>

            <button
              type="button"
              onClick={() => onTabChange("classroom")}
              className={`w-full p-2 rounded-md text-xs font-semibold flex items-center justify-between group transition-colors cursor-pointer ${
                activeTab === "classroom"
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "text-zinc-700 hover:bg-zinc-200/60"
              }`}
            >
              <div className="flex items-center gap-2">
                <UserCheck className={`w-4 h-4 ${activeTab === "classroom" ? "text-blue-600" : "text-zinc-500"}`} />
                <span>Attendance</span>
              </div>
              <ChevronRight
                className={`w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 ${
                  activeTab === "classroom" ? "text-blue-600" : "text-zinc-400"
                }`}
              />
            </button>

            <button
              type="button"
              onClick={() => onTabChange("behavior")}
              className={`w-full p-2 rounded-md text-xs font-semibold flex items-center justify-between group transition-colors cursor-pointer ${
                activeTab === "behavior"
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "text-zinc-700 hover:bg-zinc-200/60"
              }`}
            >
              <div className="flex items-center gap-2">
                <ShieldAlert className={`w-4 h-4 ${activeTab === "behavior" ? "text-blue-600" : "text-zinc-500"}`} />
                <span>Behavior</span>
              </div>
              <ChevronRight
                className={`w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 ${
                  activeTab === "behavior" ? "text-blue-600" : "text-zinc-400"
                }`}
              />
            </button>
            <button
              type="button"
              onClick={() => onTabChange("citizenship")}
              className={`w-full p-2 rounded-md text-xs font-semibold flex items-center justify-between group transition-colors cursor-pointer ${
                activeTab === "citizenship"
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "text-zinc-700 hover:bg-zinc-200/60"
              }`}
            >
              <div className="flex items-center gap-2">
                <Award className={`w-4 h-4 ${activeTab === "citizenship" ? "text-blue-600" : "text-zinc-500"}`} />
                <span>Citizenship</span>
              </div>
              <ChevronRight
                className={`w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 ${
                  activeTab === "citizenship" ? "text-blue-600" : "text-zinc-400"
                }`}
              />
            </button>
            <button
              type="button"
              onClick={() => onTabChange("dresscode")}
              className={`w-full p-2 rounded-md text-xs font-semibold flex items-center justify-between group transition-colors cursor-pointer ${
                activeTab === "dresscode"
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "text-zinc-700 hover:bg-zinc-200/60"
              }`}
            >
              <div className="flex items-center gap-2">
                <Shirt className={`w-4 h-4 ${activeTab === "dresscode" ? "text-blue-600" : "text-zinc-500"}`} />
                <span>Dress Code</span>
              </div>
              <ChevronRight
                className={`w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 ${
                  activeTab === "dresscode" ? "text-blue-600" : "text-zinc-400"
                }`}
              />
            </button>
            <button
              type="button"
              onClick={() => onTabChange("gradebook")}
              className={`w-full p-2 rounded-md text-xs font-semibold flex items-center justify-between group transition-colors cursor-pointer ${
                activeTab === "gradebook"
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "text-zinc-700 hover:bg-zinc-200/60"
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className={`w-4 h-4 ${activeTab === "gradebook" ? "text-blue-600" : "text-zinc-500"}`} />
                <span>Gradebook</span>
              </div>
              <ChevronRight
                className={`w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 ${
                  activeTab === "gradebook" ? "text-blue-600" : "text-zinc-400"
                }`}
              />
            </button>
            <button
              type="button"
              onClick={() => onTabChange("reportcard")}
              className={`w-full p-2 rounded-md text-xs font-semibold flex items-center justify-between group transition-colors cursor-pointer ${
                activeTab === "reportcard"
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "text-zinc-700 hover:bg-zinc-200/60"
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageSquareQuote className={`w-4 h-4 ${activeTab === "reportcard" ? "text-blue-600" : "text-zinc-500"}`} />
                <span>Report Card</span>
              </div>
              <ChevronRight
                className={`w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 ${
                  activeTab === "reportcard" ? "text-blue-600" : "text-zinc-400"
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
});

RightSidebar.displayName = "RightSidebar";

export default RightSidebar;